import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useMemo } from 'react'

import { ProductHeaderProps } from '~/components/UI/ProductHeader'

import { ProductHeaderProviderProps } from '~/providers/ProductPageProvider'

import useLocale from '~/hooks/useLocale'

import {
  SerializedProduct,
  fetchShopifyProductCard,
} from '~/data/product-card-data'

export const ShopifyProductContext = createContext<ShopifyProductContextValue>(
  {},
)

export interface ShopifyProductContextValue extends ProductHeaderProps {
  isLoading?: boolean
  shopifyData?: SerializedProduct
  isSuccess?: boolean
}

export default function ShopifyProductProvider({
  shopifyProduct,
  children,
}: ProductHeaderProviderProps) {
  const id = shopifyProduct?.shopifyId

  const locale = useLocale()

  const {
    data: shopifyData,
    isLoading,
    isSuccess,
  } = useQuery(
    ['get-product', id],
    async () => {
      try {
        const updatedData = await fetchShopifyProductCard({
          data: id,
          locale,
          inProductHeader: true,
          shouldFetchCollections: true,
          shouldFetchImages: true,
          shouldFetchPackMetafields: true,
        })

        return updatedData
      } catch (error) {
        console.log(error)
      }
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.log('error', err)
      },
    },
  )

  const processedValue: ShopifyProductContextValue = useMemo(
    () => ({
      shopifyData,
      isLoading,
      isSuccess,
    }),
    [isLoading, isSuccess, JSON.stringify(shopifyData)],
  )

  return (
    <ShopifyProductContext.Provider value={processedValue}>
      {children}
    </ShopifyProductContext.Provider>
  )
}

export function useShopifyProductContext() {
  return useContext(ShopifyProductContext)
}
